/**
 * /el/guides/istoselida-gamou-i-ilektroniko-prosklitirio
 *
 * The Greek counterpart of `wedding-website-vs-digital-wedding-invitation.en.js`.
 *
 * ── The question is not quite the same question in Greek ─────────────────
 * The English article compares two established product categories, because in
 * Anglophone markets both are established. In Greece the wedding website is
 * marginal and «ηλεκτρονικό προσκλητήριο» is the term that carries everything —
 * and it carries two completely different things under one name: a JPG or a PDF
 * sent on Viber, and a hosted page opened from a link.
 *
 * That is the fork a Greek couple actually stands at, and it is the one this
 * article leads with. The website comparison is still here in full, because
 * couples with guests flying in genuinely face it, but it arrives second — as
 * the question that follows the first one rather than as the premise.
 *
 * The invitation guide (`what-to-include-digital-wedding-invitation.el.js`)
 * names this ambiguity in four sentences and hands here for the rest. Do not
 * expand it there; this is the article that owns it.
 *
 * ── The rule the whole article is written under ──────────────────────────
 * NO TERMINOLOGY CLAIM IS ABSOLUTE. Providers disagree with each other, and a
 * reader who memorises a vocabulary rule here will be wrong on the next site
 * they open. Every definition is hedged — «συνήθως», «ανάλογα με το προϊόν» —
 * and the article says outright, twice, that the names are a starting point.
 * What the reader is meant to leave with is the FUNCTIONAL distinction: what
 * guests need to KNOW versus what they need to DO, and in how many places that
 * lives. Removing the hedges would make the piece confidently wrong.
 *
 * Second rule: no universal advice. Not «κάθε γάμος θέλει ιστοσελίδα», not «το
 * προσκλητήριο πάντα την αντικαθιστά». Each is a list of conditions, and each
 * list is introduced as conditions.
 *
 * ── The claims this guide is allowed to make about Agapaé ────────────────
 *   - the invitation is available on the Heirloom plan
 *   - ONE editorial design, at its own private link, in Greek and English
 *   - it can carry the couple's photographs and story, the church and the
 *     venue with map links, the schedule of the day, gift information, and the
 *     RSVP
 *   - guests reply inside the invitation; the replies become the guest list
 *   - guest management, planning and seating are part of Heirloom
 *   - guest photo sharing is part of the broader product, on every plan
 *
 * And the prohibitions, which matter more here than anywhere else in the
 * corpus because the subject invites exactly this overclaim:
 *
 *   - NOT a wedding website builder, a template platform or a CMS
 *   - NO themes or invitation templates — there is ONE design
 *   - NO custom domain and NO custom URL. The link is an auto-generated event
 *     id, not a name the couple picks. «Με τον δικό του σύνδεσμο» is the
 *     accurate phrase; anything stronger is false.
 *   - NO travel, accommodation, FAQ or dress-code SECTIONS. They are not in the
 *     public payload, and the article says plainly that the invitation is a
 *     focused page rather than a reference site — which is also the honest
 *     answer to its own "when a website adds value" section.
 *   - NO «απεριόριστα», «μόνιμα» or «για πάντα» anywhere.
 *   - NO automatic behaviour that does not exist. The couple chooses who
 *     receives the invitation and when.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const weddingWebsiteVsDigitalWeddingInvitationEl = {
  slug: "istoselida-gamou-i-ilektroniko-prosklitirio",

  title: "Ιστοσελίδα γάμου ή ηλεκτρονικό προσκλητήριο; Τι διαφέρει",
  metaTitle: "Ιστοσελίδα γάμου ή ηλεκτρονικό προσκλητήριο; | Agapaé",
  metaDescription:
    "Αρχείο, σελίδα ή ιστοσελίδα γάμου: τι κάνει το καθένα, πού μπερδεύονται, πότε αρκεί το ένα και πώς να διαλέξετε αυτό που χρειάζονται πραγματικά οι καλεσμένοι σας.",

  // The category label, and it is the sibling guide's: one cluster, one
  // eyebrow. «Ψηφιακό προσκλητήριο» is the PRODUCT page's own wording and
  // stays on the product link below, where it names the product.
  eyebrow: "Προσκλητήριο",

  hero: {
    src: "/assets/images/moments/moment_1.jpg",
    alt: "Ασπρόμαυρη φωτογραφία με το χέρι του γαμπρού να ακουμπά στο πλάι του, με τη βέρα στο δάχτυλο, και φωτεινό παράθυρο με διάφανη κουρτίνα από πίσω",
    width: 6558,
    height: 3659,
  },

  intro: [
    `Κάπου ανάμεσα στο κλείσιμο του χώρου και στη λίστα καλεσμένων, αρχίζετε να ακούτε δύο πράγματα: «ηλεκτρονικό προσκλητήριο» και «ιστοσελίδα γάμου».`,
    `Άλλοτε πωλούνται ως διαφορετικά προϊόντα, άλλοτε ως το ίδιο, και πού και πού ως κάτι τρίτο που ακούγεται σαν και τα δύο και δεν εξηγεί κανένα.`,
    `Και υπάρχει μια ακόμη σύγχυση, πιο συχνή στην Ελλάδα από την πρώτη: το «ηλεκτρονικό προσκλητήριο» σημαίνει δύο τελείως διαφορετικά πράγματα. Άλλο ένα αρχείο που στέλνετε στο Viber και άλλο μια σελίδα που ανοίγει από σύνδεσμο.`,
    `Κάτω από τα ονόματα υπάρχει πραγματική διαφορά, και δεν είναι λεξιλογική. Είναι λειτουργική: τι χρειάζεται να ξέρει ο καλεσμένος σας, τι χρειάζεται να κάνει, και σε πόσα σημεία ζουν αυτά τα δύο.`,
  ],

  sections: [
    {
      heading: "Πρώτα το πιο σημαντικό: αρχείο ή σελίδα;",
      content: [
        `Πριν καν φτάσετε στην ιστοσελίδα, υπάρχει μια πιο βασική διαφορά, και είναι αυτή που αφορά τους περισσότερους ελληνικούς γάμους.`,
        { type: "subheading", text: "Το προσκλητήριο ως αρχείο" },
        `Μια εικόνα, ένα PDF, ένα σύντομο βίντεο, μια σχεδιασμένη κάρτα που στέλνεται ως συνημμένο. Είναι απλό, και δεν ζητά τίποτα από τον καλεσμένο πέρα από το να ανοίξει ένα μήνυμα.`,
        `Τα όριά του προκύπτουν από το ότι είναι αρχείο. Μόλις σταλεί, το περιεχόμενο είναι κλειστό — μια λεπτομέρεια που αλλάζει αφήνει το αντίγραφο στα κινητά όλων λάθος. Και ένα αρχείο δεν μπορεί να συγκεντρώσει τίποτα, οπότε η απάντηση πρέπει να γίνει αλλού.`,
        { type: "subheading", text: "Το προσκλητήριο ως σελίδα" },
        `Εδώ ο καλεσμένος ανοίγει έναν σύνδεσμο και διαβάζει το προσκλητήριο μέσα στον browser του.`,
        `Επειδή φιλοξενείται αντί να σταλεί, μπορεί να κρατά περισσότερα από μια κάρτα: το πρόγραμμα της ημέρας, τους χάρτες, πληροφορία για τα δώρα, φωτογραφίες, καμιά φορά και την ιστορία σας. Συνήθως ενημερώνεται και μετά την αποστολή, και συχνά κουβαλά και το RSVP, οπότε η ανάγνωση και η απάντηση γίνονται στο ίδιο σημείο.`,
        `Αυτός είναι και ο τύπος που μπερδεύεται περισσότερο με ό,τι αλλού ονομάζεται ιστοσελίδα γάμου.`,
        `Αν λοιπόν διαβάζετε περιγραφές και δεν βγάζετε άκρη, η πρώτη ερώτηση δεν είναι πώς το λένε. Είναι: στέλνεται ή ανοίγει;`,
      ],
    },
    {
      heading: "Είναι το ίδιο η ιστοσελίδα γάμου με το προσκλητήριο;",
      content: [
        `Συνήθως όχι, αν και επικαλύπτονται πολύ και κάποια προϊόντα είναι πραγματικά και τα δύο.`,
        `Ο πιο καθαρός τρόπος να τα κρατήσετε χωριστά είναι η ερώτηση στην οποία απαντά το καθένα.`,
        `Το προσκλητήριο απαντά: θα είσαι μαζί μας;`,
        `Η ιστοσελίδα απαντά: τι πρέπει να ξέρω για αυτόν τον γάμο;`,
        `Το προσκλητήριο απευθύνεται σε συγκεκριμένους ανθρώπους και τους ζητά κάτι. Η ιστοσελίδα απευθύνεται σε όλους τους καλεσμένους και τους δίνει κάτι. Το ένα είναι σύντομο και δεμένο με μια στιγμή, το άλλο ευρύτερο και ξαναδιαβάζεται.`,
        `Το σχήμα σπάει στις άκρες, και οι άκρες είναι μεγάλες: ένα προσκλητήριο με πρόγραμμα, χάρτη και RSVP κάνει ήδη μέρος της δουλειάς μιας ιστοσελίδας.`,
        `Δείτε λοιπόν τα ονόματα ως αφετηρία και όχι ως προδιαγραφή: δεν μετράει πώς λέγεται κάτι, αλλά τι επιτρέπει στους καλεσμένους σας να μάθουν και να κάνουν.`,
      ],
    },
    {
      heading: "Τι είναι η ιστοσελίδα γάμου",
      content: [
        `Είναι συνήθως ένα ευρύτερο σημείο αναφοράς: κάπου όπου πηγαίνουν οι καλεσμένοι, και ξαναπηγαίνουν, για τη λεπτομέρεια γύρω από τη μέρα.`,
        `Το περιεχόμενο αλλάζει ανά πλατφόρμα και ζευγάρι, αλλά αυτά εμφανίζονται πιο συχνά:`,
        {
          type: "list",
          items: [
            `την ημερομηνία, τον χώρο και το πώς φτάνει κανείς`,
            `το πρόγραμμα, καμιά φορά για περισσότερες από μία μέρες`,
            `πληροφορίες μετακίνησης για όσους έρχονται από αλλού`,
            `επιλογές διαμονής, και όποια δωμάτια έχετε κρατήσει`,
            `dress code, με περισσότερη εξήγηση απ' όση χωράει σε μια κάρτα`,
            `πληροφορία για τα δώρα`,
            `συχνές ερωτήσεις`,
            `την ιστορία σας, και συχνά τους κουμπάρους`,
            `μια σελίδα RSVP, σε αρκετές πλατφόρμες`,
          ],
        },
        `Άλλες είναι μία μακριά σελίδα, άλλες πολλές με δική τους πλοήγηση. Κάποιες ανοίγουν σε όποιον έχει τη διεύθυνση, άλλες κρύβονται πίσω από κωδικό.`,
        `Δύο χαρακτηριστικά μετράνε περισσότερο από τη λίστα των ενοτήτων: είναι φτιαγμένη για να την ξαναδιαβάζουν, και φτιαγμένη για να αλλάζει. Αυτός είναι και ο πραγματικός λόγος που υπάρχει.`,
        `Στην Ελλάδα, πάντως, είναι πολύ λιγότερο συνηθισμένη απ' όσο δείχνουν τα ξένα άρθρα. Οι περισσότεροι γάμοι εδώ λύνονται με ένα καλό προσκλητήριο και λίγα μηνύματα.`,
      ],
    },
    {
      heading: "Η λειτουργική διαφορά",
      content: [
        `Αφήστε στην άκρη τα ονόματα και οι διαφορές γίνονται πρακτικές.`,
        { type: "subheading", text: "Πόση πληροφορία σηκώνει το καθένα" },
        `Το προσκλητήριο είναι λιτό από τη φύση του, και ό,τι προσθέτετε το αδυνατίζει. Μια ιστοσελίδα σηκώνει πολλά περισσότερα χωρίς αυτό το κόστος, γιατί ο κάθε καλεσμένος διαβάζει μόνο το κομμάτι για το οποίο ήρθε.`,
        { type: "subheading", text: "Πού γίνεται η απάντηση" },
        `Αυτό αλλάζει εντελώς ανά προϊόν, και είναι η λεπτομέρεια που αξίζει περισσότερο να ελέγξετε. Ένα στατικό προσκλητήριο δεν μπορεί να μαζέψει απάντηση, οπότε πρέπει να δείχνει κάπου. Μια φιλοξενούμενη σελίδα συχνά τη μαζεύει η ίδια. Μια ιστοσελίδα γάμου μπορεί να έχει δική της σελίδα RSVP, ή καμία.`,
        { type: "subheading", text: "Ενημερώσεις και διάρκεια χρήσης" },
        `Οι περισσότεροι διαβάζουν ένα προσκλητήριο μία, ίσως δύο φορές, και ενεργούν. Μια ιστοσελίδα τη συμβουλεύονται ξανά και ξανά, συχνά εβδομάδες αργότερα, όταν κάποιος κλείνει εισιτήριο ή αναρωτιέται τι να φορέσει — και περιμένουν ότι θα αλλάζει όσο προχωρά η οργάνωση.`,
        `Αυτή η διαφορά στη χρήση είναι που κρίνει αν ένας γάμος χρειάζεται καν τη δεύτερη.`,
      ],
    },
    {
      heading: "Πού μπερδεύονται",
      content: [
        `Οι κατηγορίες δεν είναι στεγανές: κάθονται πάνω σε ένα φάσμα, και πολλά προϊόντα πέφτουν στη μέση του.`,
        `Ένα φιλοξενούμενο προσκλητήριο μπορεί να έχει λεπτομέρειες, πρόγραμμα, χάρτες, πληροφορία για τα δώρα και RSVP — δηλαδή ακριβώς όσα εμφανίζονται στις λίστες με το τι πρέπει να έχει μια ιστοσελίδα γάμου. Και από την ανάποδη, κάποια ζευγάρια χρησιμοποιούν την ιστοσελίδα ως προσκλητήριο, στέλνοντας τον σύνδεσμο με ένα μήνυμα.`,
        `Κάποιες πλατφόρμες πουλούν έτσι ένα προϊόν με δύο ονόματα.`,
        `Η πιο καθαρή περίπτωση είναι ένα προσκλητήριο με ενσωματωμένο RSVP: καλεί, κρατά τις λεπτομέρειες και παίρνει την απάντηση σε ένα σημείο. Προσκλητήριο στο όνομα, και εν μέρει ιστοσελίδα στη λειτουργία.`,
        {
          type: "p",
          text: [
            `Γι' αυτό και το «ιστοσελίδα ή προσκλητήριο;» σπάνια είναι η σωστή ερώτηση. Η καλύτερη είναι τι πρέπει να κάνει η εμπειρία των καλεσμένων σας — και το αν η απάντηση θα ζει μέσα στο προσκλητήριο ή σε ξεχωριστή `,
            { text: "σελίδα RSVP", href: "/wedding-rsvp" },
            ` είναι πρακτική επιλογή και όχι λάθος κατηγορίας.`,
          ],
        },
      ],
    },
    {
      heading: "Πότε αρκεί το προσκλητήριο",
      content: [
        `Σε πολλούς γάμους, ένα καλοφτιαγμένο προσκλητήριο καλύπτει όσα χρειάζονται οι καλεσμένοι. Αυτό είναι πιθανότερο όταν ισχύουν αρκετά από αυτά:`,
        {
          type: "list",
          items: [
            `η γιορτή είναι μία κύρια εκδήλωση, ή μυστήριο και δεξίωση την ίδια μέρα`,
            `οι περισσότεροι καλεσμένοι μένουν κοντά ή ξέρουν την περιοχή`,
            `οι λεπτομέρειες που μετράνε χωράνε άνετα σε μια σελίδα`,
            `αυτό που κυρίως χρειάζεστε από τους καλεσμένους είναι η απάντηση`,
            `τα πρακτικά είναι απλά: δεν κλείνει κανείς δωμάτιο, δεν κανονίζονται μεταφορές`,
            `προτιμάτε να κρατάτε ενημερωμένο ένα σημείο αντί για δύο`,
          ],
        },
        `Τίποτα από αυτά δεν είναι κανόνας, και εξαρτάται από το είδος του προσκλητηρίου: μια εικόνα σταλμένη με μήνυμα δεν μπορεί να κουβαλήσει πρόγραμμα ούτε να πάρει απάντηση. Το «αρκεί το προσκλητήριο» ισχύει μόνο όταν το προσκλητήριο μπορεί να κάνει αυτή τη δουλειά.`,
      ],
    },
    {
      heading: "Πότε αξίζει μια ιστοσελίδα",
      content: [
        `Ένας ξεχωριστός χώρος αναφοράς δικαιώνεται όταν υπάρχουν περισσότερα να ειπωθούν απ' όσα πρέπει να σηκώσει ένα προσκλητήριο. Συνηθισμένα σημάδια:`,
        {
          type: "list",
          items: [
            `ο γάμος γίνεται σε νησί ή μακριά από εκεί όπου μένουν οι καλεσμένοι`,
            `έρχεται κόσμος από διαφορετικές χώρες, με διαφορετικές διαδρομές`,
            `υπάρχει διαμονή που πρέπει να κλείσουν μόνοι τους`,
            `η γιορτή απλώνεται σε περισσότερες από μία μέρες`,
            `επαναλαμβάνονται οι ίδιες ερωτήσεις: παρκάρισμα, μεταφορά, παιδιά, dress code`,
            `υπάρχει πληροφορία για δώρα που δεν θέλετε να τυπώσετε`,
            `οι λεπτομέρειες αλλάζουν ακόμη, και οι καλεσμένοι θα χρειαστούν την τρέχουσα εκδοχή`,
            `θα ξαναδιαβάσουν την πληροφορία περισσότερες από μία φορές`,
          ],
        },
        `Ακόμη και τότε, η ιστοσελίδα δεν είναι η μόνη απάντηση: κάποια ζευγάρια λύνουν το ίδιο με ένα μήνυμα στους καλεσμένους που αφορά ή με ένα κοινό έγγραφο.`,
        `Η αξία έρχεται από το σχήμα της πληροφορίας και όχι από την ταμπέλα του εργαλείου — πληροφορία που είναι πολλή, αλλάζει, και τη διαβάζουν ξανά.`,
      ],
    },
    {
      heading: "Πότε βγάζουν νόημα και τα δύο",
      content: [
        `Αρκετά ζευγάρια χρησιμοποιούν λογικά και τα δύο, και οι ρόλοι συνήθως μοιράζονται έτσι.`,
        `Το προσκλητήριο είναι η στιγμή και το αίτημα: αυτό που φτάνει, αυτό που διαβάζουν πρώτο, και εκεί όπου απαντούν.`,
        `Η ιστοσελίδα είναι η αναφορά που ακολουθεί: κρατά τα πρακτικά, μεγαλώνει όσο προχωρά η οργάνωση, και εκεί γυρίζουν πίσω.`,
        `Το προσκλητήριο μένει σύντομο και δείχνει παρακάτω· η ιστοσελίδα σηκώνει το βάρος.`,
        `Είναι σωστή διάταξη, αλλά όχι δωρεάν. Δύο σημεία που βλέπουν οι καλεσμένοι σημαίνουν δύο πράγματα που πρέπει να μένουν αληθινά — και το κόστος του λάθους είναι ένας καλεσμένος που διαβάζει την περσινή ώρα του μυστηρίου.`,
      ],
    },
    {
      heading: "Χρειάζεστε πραγματικά και τα δύο;",
      content: [
        `Λίγες ερωτήσεις το ξεκαθαρίζουν συνήθως. Απαντήστε τις για τον δικό σας γάμο και όχι για τους γάμους γενικά:`,
        {
          type: "list",
          items: [
            `Τι πρέπει να ξέρουν οι καλεσμένοι σας πέρα από το πότε και το πού;`,
            `Υπάρχει μετακίνηση ή διαμονή που κανονίζουν μόνοι τους;`,
            `Είναι μία μέρα ή περισσότερες;`,
            `Πού θέλετε να φτάνουν οι απαντήσεις, και είναι ένα σημείο;`,
            `Θα ψάξουν κάτι εβδομάδες αφού καλεστούν;`,
            `Θέλετε μία εστιασμένη εμπειρία ή θα συντηρήσετε δύο;`,
          ],
        },
        `Αν οι απαντήσεις σας καταλήγουν σε μια σελίδα πληροφορίας, ένα σημείο απάντησης και μία εκδήλωση, ένα καλό προσκλητήριο πιθανότατα αρκεί. Αν καταλήγουν σε ταξίδια, κρατήσεις και ερωτήσεις που δεν σταματούν, ένα σημείο στο οποίο γυρίζει κανείς πίσω δικαιώνεται μόνο του.`,
        `Καμία από τις δύο απαντήσεις δεν εξαρτήθηκε από το πώς λέγονται τα προϊόντα, αλλά από το τι πρέπει να ξέρουν οι καλεσμένοι σας, τι πρέπει να κάνουν, και σε πόσα σημεία πρέπει να ζουν αυτά.`,
      ],
    },
    {
      heading: "Αν έχετε δύο, αποφασίστε πού ζει το καθένα",
      content: [
        `Εδώ μπερδεύονται συνήθως όσοι έχουν δύο προορισμούς, οπότε αποφασίστε το πριν στείλετε οτιδήποτε και όχι αφού ρωτήσει ο πρώτος καλεσμένος.`,
        { type: "subheading", text: "Κάθε πληροφορία σε ένα σπίτι" },
        `Γράψτε τι πρέπει να ξέρουν οι καλεσμένοι σας και βάλτε το καθένα σε ένα μόνο σημείο. Το να επαναλαμβάνετε κάτι σε δύο σημεία είναι ο τρόπος με τον οποίο καταλήγουν να διαφωνούν, και η ώρα του μυστηρίου είναι αυτό που επαναλαμβάνεται πιο συχνά. Όπου κάτι πρέπει να εμφανίζεται δύο φορές, αποφασίστε ποιο αντίγραφο είναι η πηγή.`,
        `Και ό,τι δεν έχει αποφασιστεί ακόμη ανήκει στον προορισμό που μπορείτε να ενημερώσετε, ποτέ μόνο σε ένα αρχείο που κάθεται ήδη στα κινητά όλων.`,
        { type: "subheading", text: "Ένα σημείο για τις απαντήσεις" },
        {
          type: "p",
          text: [
            `Οι απαντήσεις που φτάνουν από τρεις δρόμους ταυτόχρονα είναι η κλασική αποτυχία. Διαλέξτε το ένα σημείο, πείτε το όπου μπορεί να κοιτάξει ένας καλεσμένος, και δείξτε όλα τα υπόλοιπα προς αυτό. Το `,
            {
              text: "Πώς να οργανώσετε τα RSVP του γάμου",
              href: "/guides/how-to-organize-wedding-rsvps",
            },
            ` καλύπτει τι γίνεται μόλις αρχίσουν να έρχονται.`,
          ],
        },
        { type: "subheading", text: "Κρατήστε το προσκλητήριο εστιασμένο" },
        {
          type: "p",
          text: [
            `Ένα προσκλητήριο που καλείται να κουβαλήσει ταξιδιωτικό οδηγό παύει να διαβάζεται ως προσκλητήριο. Αφήστε το να κρατά τα απαραίτητα, την απάντηση και μια ένδειξη για το πού βρίσκονται τα υπόλοιπα — και, ό,τι κι αν αποφασίσετε, τι ζητάτε και μέχρι πότε. Η `,
            {
              text: "Προθεσμία RSVP γάμου",
              href: "/guides/when-should-wedding-guests-rsvp",
            },
            ` εξηγεί πώς ορίζεται μια ημερομηνία που σας αφήνει χρόνο.`,
          ],
        },
      ],
    },
    {
      heading: "Ένα προσκλητήριο, και τι ανοίγει από πίσω του",
      content: [
        {
          type: "note",
          text: `Το ψηφιακό προσκλητήριο του Agapaé είναι διαθέσιμο στο πακέτο Heirloom.`,
        },
        {
          type: "p",
          text: [
            `Το Agapaé ακολουθεί τον δρόμο του προσκλητηρίου και όχι της ιστοσελίδας. Το `,
            {
              text: "ψηφιακό προσκλητήριο",
              href: "/digital-wedding-invitation",
            },
            ` είναι μια σελίδα με τον δικό της σύνδεσμο: οι φωτογραφίες και η ιστορία σας, η εκκλησία και ο χώρος της δεξίωσης με χάρτες, το πρόγραμμα της ημέρας, πληροφορία για τα δώρα αν τη θέλετε, και το RSVP στο τέλος.`,
          ],
        },
        `Αξίζει να είμαστε ακριβείς για το τι δεν είναι. Το Agapaé δεν είναι εργαλείο κατασκευής ιστοσελίδας γάμου: δεν υπάρχει σελίδα να στήσετε κομμάτι κομμάτι, δεν υπάρχει βιβλιοθήκη με πρότυπα, δεν υπάρχει δικό σας domain να συντηρήσετε. Ένας σχεδιασμός, στα ελληνικά και στα αγγλικά, κρατά τα δικά σας στοιχεία, και οι καλεσμένοι τον ανοίγουν στον browser που έχουν ήδη.`,
        `Αυτή η εστίαση έχει ένα όριο που αξίζει να ξέρετε: το προσκλητήριο είναι προσκλητήριο, όχι ταξιδιωτικός οδηγός. Ένας γάμος με σοβαρά ζητήματα μετακίνησης και διαμονής μπορεί να θέλει και έναν ξεχωριστό χώρο γι' αυτά.`,
        { type: "subheading", text: "Τι ανοίγει από πίσω" },
        {
          type: "p",
          text: [
            `Οι καλεσμένοι απαντούν μέσα στο προσκλητήριο, για κάθε άτομο μαζί τους και για κάθε στιγμή του γάμου, και αυτές οι απαντήσεις γίνονται η λίστα σας αντί για ένα αρχείο Excel. Τα ίδια ονόματα συνεχίζουν στα κομμάτια του Heirloom που δουλεύετε ιδιωτικά: τη λίστα καλεσμένων, την `,
            { text: "οργάνωση", href: "/wedding-planning" },
            ` και τα τραπέζια σας. Αργότερα, οι φωτογραφίες των καλεσμένων φέρνουν ό,τι τράβηξαν όλοι εκείνη τη μέρα, που ανήκει στο ευρύτερο προϊόν και όχι στο προσκλητήριο.`,
          ],
        },
        `Δεν αγγίζει κάθε καλεσμένος κάθε κομμάτι: οι περισσότεροι βλέπουν το προσκλητήριο, απαντούν, και πολύ αργότερα μοιράζονται τις φωτογραφίες τους. Η πλευρά της οργάνωσης είναι δική σας.`,
      ],
    },
    {
      heading: "Μια σύντομη λίστα απόφασης",
      content: [
        { type: "subheading", text: "Το προσκλητήριο μόνο του μπορεί να αρκεί αν" },
        {
          type: "list",
          items: [
            `η γιορτή σας είναι μία εκδήλωση, σε έναν τόπο, με απλή διαδρομή`,
            `τα απαραίτητα χωράνε σε μια σελίδα, και αυτό που κυρίως θέλετε είναι η απάντηση`,
            `προτιμάτε να συντηρείτε ένα σημείο αντί για δύο`,
          ],
        },
        { type: "subheading", text: "Μια ιστοσελίδα μπορεί να αξίζει αν" },
        {
          type: "list",
          items: [
            `οι καλεσμένοι ταξιδεύουν μακριά ή κλείνουν μόνοι τους διαμονή`,
            `η γιορτή απλώνεται σε πολλές μέρες ή πολλούς χώρους`,
            `υπάρχει λεπτομέρεια που αλλάζει ή που θα ψάξουν αργότερα`,
          ],
        },
        { type: "subheading", text: "Σκεφτείτε και τα δύο αν" },
        {
          type: "list",
          items: [
            `υπάρχουν πραγματικά περισσότερα να ειπωθούν απ' όσα σηκώνει ένα προσκλητήριο`,
            `μπορείτε να πείτε από τώρα ποιος προορισμός κρατά ποια πληροφορία`,
            `σας βολεύει να κρατάτε δύο πράγματα σωστά μέχρι την ημέρα`,
          ],
        },
      ],
    },
    {
      heading: "Λειτουργία πάνω από ονομασία",
      content: [
        `Η διαφορά που αξίζει να θυμάστε δεν είναι λεξιλογικός κανόνας. Το προσκλητήριο κάνει μια ερώτηση στους καλεσμένους σας· ένας χώρος αναφοράς απαντά στις δικές τους.`,
        `Τα περισσότερα προϊόντα κάνουν περισσότερο το ένα από το άλλο, και το όνομα στη σελίδα δεν θα σας πει πάντα ποιο.`,
        `Κοιτάξτε λοιπόν τι επιτρέπει πραγματικά ένα προϊόν στους καλεσμένους σας να μάθουν και να κάνουν, αποφασίστε πόσους προορισμούς χρειάζεται τίμια ο γάμος σας, και δώστε στην απάντηση ένα καθαρό σπίτι.`,
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Είναι το ίδιο η ιστοσελίδα γάμου με το ηλεκτρονικό προσκλητήριο;`,
      a: `Συνήθως όχι, αν και επικαλύπτονται και κάποιες πλατφόρμες πουλούν ένα προϊόν με δύο ονόματα. Το προσκλητήριο είναι κανονικά η ίδια η πρόσκληση: καλεί, κρατά τα απαραίτητα και, ανάλογα με το προϊόν, παίρνει την απάντηση. Η ιστοσελίδα είναι κανονικά ένας ευρύτερος χώρος αναφοράς για μετακίνηση, διαμονή και πρόγραμμα.`,
    },
    {
      q: `Μπορεί ένα ηλεκτρονικό προσκλητήριο να έχει RSVP μέσα;`,
      a: `Πολλά μπορούν, αλλά όχι όλα. Ένα προσκλητήριο που ανοίγει από σύνδεσμο συχνά μαζεύει την απάντηση μέσα του. Ένα που στέλνεται ως εικόνα, PDF ή βίντεο δεν μπορεί να μαζέψει τίποτα, οπότε η απάντηση πρέπει να γίνει αλλού. Αν σας ενδιαφέρει, ελέγξτε το πριν διαλέξετε.`,
    },
    {
      q: `Χρειάζομαι και ιστοσελίδα και προσκλητήριο;`,
      a: `Όχι απαραίτητα. Εξαρτάται από το πόσα πρέπει να ξέρουν οι καλεσμένοι σας πέρα από το πότε και το πού. Ένας γάμος με απλά πρακτικά καλύπτεται συνήθως από ένα προσκλητήριο που κρατά τις λεπτομέρειες και την απάντηση. Το ταξίδι, η διαμονή, οι πολλές μέρες ή η πληροφορία που αλλάζει είναι που δικαιολογούν έναν δεύτερο προορισμό.`,
    },
    {
      q: `Μπορεί το προσκλητήριο να αντικαταστήσει την ιστοσελίδα;`,
      a: `Κάποιες φορές, και εξαρτάται από το προσκλητήριο. Ένα φιλοξενούμενο προσκλητήριο με πρόγραμμα, χάρτες, πληροφορία για τα δώρα και RSVP καλύπτει ήδη πολλά από όσα μπαίνουν αλλιώς σε ιστοσελίδα. Αυτό που συνήθως δεν αντικαθιστά είναι έναν αναλυτικό οδηγό μετακίνησης, επιλογές διαμονής ή μια μακριά λίστα ερωτήσεων.`,
    },
    {
      q: `Τι να διαλέξω αν έρχεται πολύς κόσμος από μακριά;`,
      a: `Όταν οι καλεσμένοι κλείνουν μόνοι τους εισιτήρια και δωμάτια, χρειάζονται κάπου να γυρίζουν, οπότε ένας χώρος αναφοράς συνήθως αξίζει. Δεν χρειάζεται να είναι ολόκληρη ιστοσελίδα: ένα μήνυμα στους καλεσμένους που αφορά ή ένα κοινό έγγραφο κάνουν την ίδια δουλειά. Κρατήστε το προσκλητήριο στην πρόσκληση και στην απάντηση.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: [
      "what-to-include-digital-wedding-invitation",
      "how-to-organize-wedding-rsvps",
    ],

    product: [
      {
        href: "/digital-wedding-invitation",
        label: "Ψηφιακό προσκλητήριο",
        blurb:
          "Δείτε το ίδιο το προσκλητήριο: ένας σχεδιασμός με τον δικό του σύνδεσμο, που κρατά την ιστορία σας, τους χώρους, το πρόγραμμα της ημέρας και το RSVP. Διαθέσιμο στο πακέτο Heirloom.",
      },
      {
        href: "/wedding-rsvp",
        label: "RSVP γάμου",
        blurb:
          "Δείτε πώς απαντούν οι καλεσμένοι μέσα στο προσκλητήριο, ανά άτομο και ανά στιγμή, και πώς οι απαντήσεις γίνονται η λίστα πάνω στην οποία οργανώνετε τη μέρα.",
      },
    ],
  },
};

export default weddingWebsiteVsDigitalWeddingInvitationEl;
